/* eslint-disable max-classes-per-file */
/* eslint-disable camelcase */

import axios from 'axios';
import { Decimal } from 'decimal.js';
import { keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';
import type { SiteId } from '@hotjar/site';
import type {
  CostSpecDTO,
  AppliedDeductionDTO,
  SalesTaxInfoDTO,
  PriceSummaryDTO,
  SubscriptionChangePriceSummaryDTO,
  LineItemDTO,
} from '@hotjar/webapp/domains/billing/index';
import { ApplicationType } from '@hotjar/webapp/domains/billing/index';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { mapPlanDtoToModel } from '@hotjar/webapp/domains/revenue/plan/mapper';
import { hasBundleDiscount } from '@hotjar/webapp/domains/billing/discounts';
import { isCardExpiringSoon } from '@hotjar/webapp/domains/billing/paymentMethod';
import type {
  SubscriptionDto,
  Prices,
  SubscriptionInfo,
} from '@hotjar/webapp/domains/common/subscription/types';
import type { Settings } from '@hotjar/webapp/domains/common/settings';
import type { VaultedPaymentMethod } from './payment';

class RawCostSpec {
  siteId: SiteId;

  planId: string;

  alwaysOnRate: number;

  constructor(siteId: SiteId, planId: string, alwaysOnRate: number) {
    this.siteId = siteId;
    this.planId = planId;
    this.alwaysOnRate = alwaysOnRate;
  }

  static fromCostSpec(costSpec: CostSpecDTO): RawCostSpec {
    const siteId = costSpec.site_id ?? 0;
    return new RawCostSpec(
      typeof siteId === 'number' ? siteId : parseInt(siteId, 10),
      costSpec.plan_id,
      costSpec.always_on_rate,
    );
  }
}

export class Deduction {
  id: string;

  name: unknown;

  application: ApplicationType;

  percentage: string;

  amount: Decimal;

  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  constructor(
    id: string,
    name: string,
    application: ApplicationType = ApplicationType.ONCE,
    amount = new Decimal('0'),
    percentage = '0',
  ) {
    this.id = id;
    this.name = name;
    this.application = application;
    this.amount = amount;
    this.percentage = percentage;
  }

  static fromResponse({ deduction, amount }: AppliedDeductionDTO): Deduction {
    const { id, name, application, percentage } = deduction;
    return new Deduction(id, name, application, new Decimal(amount), percentage);
  }
}

export class SalesTax {
  rate: number;

  amount: Decimal;

  constructor(rate = 0, amount = new Decimal('0')) {
    this.rate = rate;
    this.amount = amount;
  }

  static fromResponse(response: SalesTaxInfoDTO): SalesTax {
    return new SalesTax(response.vat_rate, new Decimal(response.vat_amount));
  }
}

/**
 * Class which holds the data for sites cost summary
 * todo(Note): This is a copy of the CostSummary that exists in the billingV2-ctrl.js
 */
export class CostSummary {
  total: Decimal;

  subtotal: Decimal;

  discounted: boolean;

  yearlyDiscount;

  constructor(total: Decimal, subtotal: Decimal, yearlyDiscount: Deduction | undefined) {
    this.total = total;
    this.subtotal = subtotal;
    this.yearlyDiscount = yearlyDiscount;
    this.discounted = total && !total.eq(subtotal);
  }

  /**
   * Factory for constructing a CostSummary from a PriceSummary which is
   * the result of previewing the cost of a set of plans for the Account.
   */
  static fromPriceSummary(priceSummary: PriceSummary): CostSummary {
    const { total, subtotal } = priceSummary;
    const yearlyDiscount = priceSummary.deductions.find(({ id }) => id === 'ANNUAL_DEDUCTION');

    return new CostSummary(total, subtotal, yearlyDiscount);
  }
}

interface SitePriceInfoParams {
  total: Decimal;
  subtotal: Decimal;
  discountPercentage: number;
  legacyDiscount: Decimal | undefined;
  planId: Plan['identifier'] | undefined;
}

/**
 * Class which encapsulates the data which describes a
 * Site's cost. This will get updated if the user selects a new plan.
 * todo(Note): This is a copy of the SitePriceInfo that exists in the billingV2-ctrl.js
 */
export class SitePriceInfo {
  total: Decimal;

  subtotal: Decimal;

  discountPercentage: number;

  showSubtotal: boolean;

  showPercentage: boolean;

  legacyDiscount: Decimal | undefined;

  bundleDiscount: boolean;

  constructor({
    total,
    subtotal,
    discountPercentage,
    legacyDiscount,
    planId,
  }: SitePriceInfoParams) {
    const hasLegacyDiscount = !legacyDiscount?.eq(0);

    this.total = total;
    this.subtotal = subtotal;
    this.discountPercentage = discountPercentage;
    this.showSubtotal = total.gt(0) && !total.eq(subtotal) && !hasLegacyDiscount;
    this.showPercentage = discountPercentage > 0;
    this.legacyDiscount = hasLegacyDiscount ? legacyDiscount : undefined;
    this.bundleDiscount = hasBundleDiscount(planId);
  }

  /**
   * Factory for constructing a SitePriceInfo from an optional
   * LineItem
   */
  static fromLineItem(maybeLineItem: LineItem | undefined): SitePriceInfo {
    return new SitePriceInfo({
      total: maybeLineItem?.total ?? new Decimal(0),
      subtotal: maybeLineItem?.subtotal ?? new Decimal(0),
      discountPercentage: maybeLineItem?.discountPercentage ?? 0,
      legacyDiscount:
        maybeLineItem?.deductions.find(({ id }) => id === 'LEGACY')?.amount ?? new Decimal(0),
      planId: maybeLineItem?.sitePlanInfo.plan.identifier,
    });
  }
}

interface SiteReference {
  name: string;
  organizationId: number;
  siteId: number;
}

export class SitePlanInfo {
  plan: Plan;

  siteReference: SiteReference;

  constructor(plan: Plan, siteReferences: SiteReference) {
    this.plan = plan;
    this.siteReference = siteReferences;
  }

  static fromResponse(response: LineItemDTO['site_plan_info']): SitePlanInfo {
    // FIXME: camelcase
    // eslint-disable-next-line camelcase
    const { name, organization_id, site_id } = response.site_reference;
    return new SitePlanInfo(mapPlanDtoToModel(response.plan), {
      name,
      organizationId: organization_id,
      siteId: site_id,
    });
  }
}

export class LineItem {
  baseCostPerUnit: Decimal;

  costPerUnit: Decimal;

  subtotal: Decimal;

  total: Decimal;

  units: number;

  deductions: Deduction[];

  sitePlanInfo: SitePlanInfo;

  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  constructor(
    /* eslint-disable @typescript-eslint/default-param-last */
    baseCostPerUnit: Decimal = new Decimal('0'),
    costPerUnit: Decimal = new Decimal('0'),
    subtotal: Decimal = new Decimal('0'),
    total: Decimal = new Decimal('0'),
    units = 0,
    deductions: Deduction[] = [],
    /* eslint-enable @typescript-eslint/default-param-last */
    sitePlanInfo: SitePlanInfo,
  ) {
    this.baseCostPerUnit = baseCostPerUnit;
    this.costPerUnit = costPerUnit;
    this.subtotal = subtotal;
    this.total = total;
    this.units = units;
    this.deductions = deductions;
    this.sitePlanInfo = sitePlanInfo;
  }

  get discountPercentage(): number {
    return this.deductions.reduce((sum, discount) => sum + parseInt(discount.percentage, 10), 0);
  }

  static fromResponse(response: LineItemDTO): LineItem {
    const {
      // FIXME: camelcase
      // eslint-disable-next-line camelcase
      base_cost_per_unit,
      // FIXME: camelcase
      // eslint-disable-next-line camelcase
      cost_per_unit,
      subtotal,
      total,
      deductions,
      // FIXME: camelcase
      // eslint-disable-next-line camelcase
      site_plan_info,
      units,
    } = response;
    return new LineItem(
      new Decimal(base_cost_per_unit),
      new Decimal(cost_per_unit),
      new Decimal(subtotal),
      new Decimal(total),
      units,
      deductions.map(Deduction.fromResponse),
      SitePlanInfo.fromResponse(site_plan_info),
    );
  }
}

export class PriceSummary {
  subtotal: Decimal;

  deductions: Deduction[];

  tax: SalesTax;

  total: Decimal;

  totalWithTax: Decimal;

  isProrated: boolean;

  lineItems: LineItem[];

  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  constructor(
    subtotal: Decimal = new Decimal('0'),
    deductions: Deduction[] = [],
    tax: SalesTax = new SalesTax(),
    total: Decimal = new Decimal('0'),
    totalWithTax: Decimal = new Decimal('0'),
    isProrated = false,
    lineItems: LineItem[] = [],
  ) {
    this.subtotal = subtotal;
    this.deductions = deductions;
    this.tax = tax;
    this.total = total;
    this.totalWithTax = totalWithTax;
    this.isProrated = isProrated;
    this.lineItems = lineItems;
  }

  get deductableTotal(): Decimal {
    return this.deductions.reduce(
      (total, deduction) => total.plus(deduction.amount),
      new Decimal('0'),
    );
  }

  get discounts(): Deduction[] {
    return this.deductions.filter((deduction) => deduction.percentage);
  }

  get discountPercentage(): number {
    return this.discounts.reduce((sum, discount) => sum + parseInt(discount.percentage, 10), 0);
  }

  get discountTotal(): Decimal {
    return this.discounts.reduce(
      (total, deduction) => total.plus(deduction.amount),
      new Decimal('0'),
    );
  }

  static fromResponse(response: PriceSummaryDTO): PriceSummary {
    return new PriceSummary(
      new Decimal(response.subtotal),
      response.deductions.map(Deduction.fromResponse),
      SalesTax.fromResponse(response.tax_info),
      new Decimal(response.total),
      new Decimal(response.total_with_tax),
      response.is_prorated,
      response.line_items.map(LineItem.fromResponse),
    );
  }
}

export class SubscriptionChangePriceSummary {
  current: PriceSummary;

  charge: PriceSummary;

  updated: PriceSummary;

  future: PriceSummary;

  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  constructor(
    current = new PriceSummary(),
    charge = new PriceSummary(),
    updated = new PriceSummary(),
    future = new PriceSummary(),
  ) {
    this.current = current;
    this.charge = charge;
    this.updated = updated;
    this.future = future;
  }

  static fromResponse(response: SubscriptionChangePriceSummaryDTO): SubscriptionChangePriceSummary {
    return new SubscriptionChangePriceSummary(
      PriceSummary.fromResponse(response.current),
      PriceSummary.fromResponse(response.charge),
      PriceSummary.fromResponse(response.updated),
      PriceSummary.fromResponse(response.future),
    );
  }
}

const prepareForRequest = keysToSnakeCase;

export interface AccountOverrides {
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  account_type?: 'individual' | 'agency';
  [key: string]: string | undefined;
}

interface Action {
  action: string;
}

export interface SubscriptionActionRequestPayload extends Action {
  action: 'preview';
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  cost_specs: RawCostSpec[] | undefined;
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  billing_cycle: IBillingCycle | undefined;
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  account_overrides: AccountOverrides | undefined;
}
/**
 * Return preview subscription action request payload
 *
 * @param costSpecs - The list of cost specs that would be applied
 * @param billingCycle - the account's billing cycle
 * @param accountOverrides - account overrides to return the subscription in a different future state.
 *  Should either be undefined or contain at least one key/value pair.
 * @returns A subscription action request payload
 */
const previewSubscriptionAction = (
  costSpecs: CostSpecDTO[] | undefined,
  billingCycle: IBillingCycle | undefined,
  accountOverrides: AccountOverrides | undefined,
): SubscriptionActionRequestPayload => ({
  action: 'preview',
  cost_specs:
    costSpecs &&
    (costSpecs.map((costSpec) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      prepareForRequest<any>(RawCostSpec.fromCostSpec(costSpec)),
    ) as RawCostSpec[]),
  billing_cycle: billingCycle,
  // NOTE: passing null values does not revert to getting the key on the current account. It will treat it as an empty string
  // and calculate the preview accordingly.
  account_overrides: accountOverrides,
});

interface SubscriptionUpsertConfig {
  costSpecs: SitePlanInfo[];
  accountId: number;
  paymentMethod?: VaultedPaymentMethod;
  billingCycle?: IBillingCycle;
  poNumber?: string;
}

export interface SubscriptionUpsertAction {
  action: 'upsert_subscription';
  cost_specs: SitePlanInfo[];
  billing_cycle?: IBillingCycle;
  payment_method_type?: string;
  payment_method_nonce?: string;
  payment_method_token?: string;
  po_number?: string;
}

const upsertSubscriptionAction = ({
  costSpecs,
  paymentMethod,
  billingCycle,
  poNumber,
}: Omit<SubscriptionUpsertConfig, 'accountId'>): SubscriptionUpsertAction => ({
  action: 'upsert_subscription',
  cost_specs: costSpecs,
  billing_cycle: billingCycle,
  payment_method_type: paymentMethod?.paymentType,
  payment_method_nonce: paymentMethod?.nonce,
  payment_method_token: paymentMethod?.token,
  po_number: poNumber,
});

export const subscriptionAPI = {
  // accountOverrides should either be undefined or contain at least one key/value pair.
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  preview: (
    account: { id?: number | undefined },
    costSpecs: CostSpecDTO[],
    billingCycle: IBillingCycle | undefined,
    accountOverrides?: AccountOverrides,
  ): Promise<SubscriptionChangePriceSummary> =>
    axios
      .post<SubscriptionChangePriceSummaryDTO & ApiBaseResponse>(
        `/api/v1/accounts/${account.id}/subscription`,
        previewSubscriptionAction(costSpecs, billingCycle, accountOverrides),
      )
      .then((response) =>
        response.data.success
          ? SubscriptionChangePriceSummary.fromResponse(response.data)
          : Promise.reject(response.data.error),
      ),
  getProRata: (
    accountId: number,
    planId: string,
    organizationId: number | string,
  ): Promise<{ success: boolean; totalCost: number }> => {
    return axios
      .post<
        ApiBaseResponse & {
          total_cost: number;
        }
      >(`/api/v1/accounts/${accountId}/payment/details`, {
        action: 'calculate_pro_rata_new_site',
        plan_id: planId,
        organization_id: organizationId,
      })
      .then((response) => ({
        success: response.data.success,
        totalCost: response.data.total_cost,
      }));
  },
  upsert: (config: SubscriptionUpsertConfig): Promise<null> => {
    const { accountId, ...action } = config;

    return axios
      .post<ApiBaseResponse>(
        `/api/v1/accounts/${accountId}/payment/details`,
        upsertSubscriptionAction(action),
      )
      .then((response) => (response.data.success ? null : Promise.reject(response.data.error)));
  },
  getInfo: (accountId: number, settings?: Settings): Promise<SubscriptionInfo> => {
    const currencySymbols = settings?.currency_symbols;
    return axios
      .get<SubscriptionDto>(`/api/v1/accounts/${accountId}/payment/details`)
      .then((res) => res.data)
      .then((res) => {
        const prices: Prices = {
          currency: res.currency,
          symbol: currencySymbols?.[res.currency] || '',
          vatRate: Number(res.vat_rate),
          total: 0,
          vatTotal: 0,
          subtotal: 0,
        };

        // TODO(rob.lester): Delete all this price stuff, we
        // need to call the subscription preview (or pricing)
        // API for more accurate numbers. These numbers won't
        // include the right deductions.
        if (res.billing_cycle === 'yearly_recurring') {
          prices.total = Number(res.yearly_subtotal) + Number(res.yearly_vat_amount);
          prices.vatTotal = Number(res.yearly_vat_amount);
          prices.subtotal = Number(res.yearly_subtotal);
        } else {
          prices.total = Number(res.monthly_subtotal) + Number(res.monthly_vat_amount);
          prices.vatTotal = Number(res.monthly_vat_amount);
          prices.subtotal = Number(res.monthly_subtotal);
        }

        const subscriptionInfo = {
          accountOwner: res.account_owner,
          billingCycle: res.billing_cycle,
          nextBillingDate: res.next_billing_date,
          hasPaidPlan: res.has_paid_plan,
          paymentMethod: {
            available: res.payment_method_type !== null,
            expiryDate: res.card_expiration_date,
            label: res.payment_method_label,
            type: res.payment_method_type,
          },
          prices,
          recurringPayment: {
            failureCount: res.failure_count,
            lastFailureDate: res.failure_datetime,
          },
          warnings: {
            cardExpiringSoon: isCardExpiringSoon(res.card_expiration_date),
            pastDue: res.has_paid_plan && res.failure_count > 0,
          },
        };

        return subscriptionInfo;
      });
  },
};
